import {
  InMemoryCache,
  type Cache,
  type InMemoryCacheConfig,
  type NormalizedCacheObject,
  type OperationVariables,
  type Reference,
  type StoreObject,
} from '@apollo/client';
import {
  createFragmentMap,
  getDefaultValues,
  getFragmentDefinitions,
  getFragmentFromSelection,
  getOperationDefinition,
  isField,
  isReference,
  resultKeyNameFromField,
} from '@apollo/client/utilities';
import type { DocumentNode, FieldNode, SelectionSetNode } from 'graphql';

/**
 * Caché de Apollo con DOS relojes (spec 2026-10-01 § 7.1, Fase 3b):
 *
 * - **Frescura**: un campo raíz de más de `freshnessMs` se informa como
 *   INCOMPLETO en `diff`. Así las consultas `cache-first` (243 en `src/`) vuelven
 *   a la red, igual que con el TTL de antes.
 * - **Lectura directa** (`read`/`readQuery`): con servidor alcanzable, lo
 *   caducado se lee como AUSENTE (`null`), igual que con la librería. Así los
 *   17 loaders «readQuery primero» (`isCacheEmpty`) siguen yendo a la red a
 *   las 3 h en vez de dar por buena una lista vieja.
 * - **Retención**: lo caducado NO se borra al leerlo. `readStale` lo devuelve
 *   siempre, y es lo que usa `OfflineCacheFallbackLink` para enseñarlo cuando
 *   la red falla o se cuelga (stale-if-error, RFC 5861). Solo lo quita
 *   `pruneRetention`, por edad, para que el snapshot no crezca sin fin.
 * - **Respuesta de red sobre un campo caducado**: antes de escribirla se quita
 *   ese campo, como hacía la librería al leer. Si no, una lista paginada
 *   (`cursor`/`take` fuera de `keyArgs`, un solo cajón por filtro) MEZCLARÍA lo
 *   nuevo con lo viejo y sellaría el cajón entero como fresco: filas fantasma,
 *   un filtro vaciado que sigue enseñando lo de antes, páginas 2..N que nunca
 *   se renuevan.
 *
 * Sustituye a `InvalidationPolicyCache` (`@nerdwallet/apollo-cache-policies`,
 * archivada), que BORRABA el dato en la primera lectura caducada.
 *
 * Quién rejuvenece un dato: SOLO la respuesta de red. `FreshnessLink` marca el
 * objeto `result.data` (`markFromNetwork`) y la edad se pone cuando Apollo lo
 * ESCRIBE en la caché, con el documento de la caché: Apollo quita `@connection`
 * del documento que envía a los links pero escribe con el original, así que
 * calcular las claves en el link marcaría el cubo equivocado. Un resultado que
 * nunca se escribe (`no-cache`) no pone edad. Si el objeto se clonase por el
 * camino, se pierde la marca y el fallo es conservador (queda caducado).
 * Una escritura local —un `update` de la app o la reescritura que hace Apollo
 * del dato servido por el respaldo— nunca lo rejuvenece: si no, lo caducado
 * servido sin red parecería recién pedido.
 *
 * Límites conocidos: reproducir capas optimistas con `removeOptimistic` puede
 * sellar (hoy no hay `optimisticResponse` en `src/`); y una consulta
 * `cache-only` sobre un campo caducado con el servidor alcanzable da parcial
 * sin datos, salvo que pida `returnPartialData`.
 * Un poll cuyo resultado es idéntico al anterior no lo escribe Apollo
 * (`QueryInfo.shouldWrite`), así que no renueva la edad: es conservador (a lo
 * sumo una petición de más de los lectores cache-first de ese campo, nunca un
 * dato viejo tomado por fresco); `refetch()` sí renueva.
 * Medido en el spike del 2026-10-01: con la regla de la librería (toda
 * escritura renueva) el respaldo lo «rejuvenecía» y no se volvía a pedir.
 * Por lo mismo, un campo sondeado con respuesta idéntica durante 7 días lo
 * poda la retención; el siguiente sondeo lo repone.
 * Una consulta `cache-and-network` con dato caducado no pinta lo cacheado al
 * instante (no hay `returnPartialData`): espera a la red o al respaldo, como
 * con la librería.
 * Una respuesta con `errors` y `errorPolicy` `all`/`ignore` no prueba
 * frescura: se escribe sin edad de red.
 * `@skip`/`@include` en un campo raíz no se tienen en cuenta al medir la edad
 * (hoy no hay ninguno en `src/`).
 */

/** Clave del registro de edades dentro del snapshot persistido. */
export const FRESHNESS_SNAPSHOT_KEY = '__freshness';

const ROOT_QUERY = 'ROOT_QUERY';

interface FreshnessSnapshot {
  v: 1;
  /** `storeFieldName` de ROOT_QUERY → ms desde epoch de la última respuesta de red. */
  roots: Record<string, number>;
}

/** Forma del bloque `invalidation` que dejaba la librería en el snapshot. */
interface LegacyInvalidation {
  entitiesById?: Record<
    string,
    {
      dataId?: string;
      cacheTime?: number;
      storeFieldNames?: { entries?: Record<string, { cacheTime?: number }> };
    }
  >;
}

/** Solo se aceptan edades que son números finitos de un registro de la versión 1. */
function validRoots(snapshot: unknown): Array<[string, number]> {
  const candidate = snapshot as Partial<FreshnessSnapshot> | undefined;
  if (!candidate || candidate.v !== 1 || !candidate.roots || typeof candidate.roots !== 'object') return [];
  return Object.entries(candidate.roots).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1]),
  );
}

export interface FreshnessCacheConfig extends InMemoryCacheConfig {
  /** Edad a partir de la cual un dato se vuelve a pedir cuando hay red. */
  freshnessMs: number;
}

interface TopLevelField {
  field: FieldNode;
  fieldName: string;
  resultKey: string;
}

const topLevelFieldsMemo = new WeakMap<DocumentNode, TopLevelField[]>();

/** Campos raíz de una consulta, aplanando los fragmentos del primer nivel. */
function topLevelFields(query: DocumentNode): TopLevelField[] {
  const memo = topLevelFieldsMemo.get(query);
  if (memo) return memo;
  const fields: TopLevelField[] = [];
  const operation = getOperationDefinition(query);
  if (operation && operation.operation === 'query') {
    const fragmentMap = createFragmentMap(getFragmentDefinitions(query));
    const visit = (selectionSet: SelectionSetNode) => {
      for (const selection of selectionSet.selections) {
        if (isField(selection)) {
          if (selection.name.value === '__typename') continue;
          fields.push({
            field: selection,
            fieldName: selection.name.value,
            resultKey: resultKeyNameFromField(selection),
          });
        } else {
          const fragment = getFragmentFromSelection(selection, fragmentMap);
          if (fragment) visit(fragment.selectionSet);
        }
      }
    };
    visit(operation.selectionSet);
  }
  topLevelFieldsMemo.set(query, fields);
  return fields;
}

export class FreshnessCache extends InMemoryCache {
  readonly freshnessMs: number;
  private rootStamps = new Map<string, number>();
  /**
   * Edad por entidad, solo en memoria. Sirve para los campos raíz que resuelve
   * un `read` con `toReference` (el detalle sacado de la lista, p. ej.
   * `order(id)`), que no tienen campo propio en ROOT_QUERY hasta que se piden.
   * Al restaurar se reconstruye desde los campos raíz.
   */
  private entityStamps = new Map<string, number>();
  private freshnessEnforced = true;
  private broadcasting = false;
  private optimisticDepth = 0;
  private staleReads = 0;
  /** Objetos `result.data` marcados como respuesta de red, pendientes de escribirse. */
  private networkResults = new WeakSet<object>();

  constructor({ freshnessMs, ...config }: FreshnessCacheConfig) {
    super(config);
    this.freshnessMs = freshnessMs;
  }

  /**
   * Con servidor alcanzable lo caducado se vuelve a pedir; sin él se sirve
   * de la caché sin intentarlo. Sustituye a los interruptores de la Fase 3a y,
   * a diferencia de ellos, sobrevive a `reset()`.
   */
  enforceFreshness(online: boolean): void {
    this.freshnessEnforced = online;
  }

  isFreshnessEnforced(): boolean {
    return this.freshnessEnforced;
  }

  /** Edad registrada de un campo raíz (diagnóstico y tests). */
  rootStampOf(storeFieldName: string): number | undefined {
    return this.rootStamps.get(storeFieldName);
  }

  /**
   * Lectura directa. Con servidor alcanzable, una consulta de ROOT_QUERY con
   * algún campo raíz caducado se lee como ausente (`null`), igual que con la
   * librería: es la señal con la que los loaders «readQuery primero» van a la
   * red. `readStale` la salta.
   */
  read<TData = unknown>(options: Cache.ReadOptions): TData | null {
    const result = super.read<TData>(options);
    if (result === null || this.staleReads > 0 || !this.freshnessEnforced || this.broadcasting) {
      return result;
    }
    if ((options.rootId ?? ROOT_QUERY) !== ROOT_QUERY) return result;
    const oldest = this.oldestStampFor(
      options.query,
      options.variables as Record<string, unknown> | undefined,
      result as Record<string, unknown>,
    );
    return oldest !== undefined && Date.now() > oldest + this.freshnessMs ? null : result;
  }

  /**
   * Lee ignorando la frescura (stale-if-error). Solo para el respaldo sin red:
   * el resto de la app debe ver lo caducado como ausente para ir a la red.
   */
  readStale<T>(read: () => T): T {
    this.staleReads += 1;
    try {
      return read();
    } finally {
      this.staleReads -= 1;
    }
  }

  diff<TData, TVariables extends OperationVariables = any>(
    options: Cache.DiffOptions<TData, TVariables>,
  ): Cache.DiffResult<TData> {
    const diff = super.diff<TData, TVariables>(options);
    // Las relecturas de `broadcastWatches` no deciden si ir a la red: si las
    // marcásemos incompletas, cualquier escritura ajena tocaría a las pantallas
    // montadas (mismo criterio que la librería).
    if (!diff.complete || !this.freshnessEnforced || this.broadcasting) return diff;
    // Fragmentos (`useFragment`) y lecturas por id: nunca van a la red.
    if ((options.id ?? ROOT_QUERY) !== ROOT_QUERY) return diff;
    const oldest = this.oldestStampFor(
      options.query,
      options.variables as Record<string, unknown> | undefined,
      diff.result as Record<string, unknown> | undefined,
    );
    if (oldest !== undefined && Date.now() > oldest + this.freshnessMs) {
      // Copia: el resultado de `diff` está memoizado y no se puede tocar.
      return { ...diff, complete: false };
    }
    return diff;
  }

  write(options: Cache.WriteOptions): Reference | undefined {
    const result = options.result as object | null | undefined;
    const fromNetwork = !!result && typeof result === 'object' && this.networkResults.delete(result);
    if (fromNetwork && this.optimisticDepth === 0 && (options.dataId ?? ROOT_QUERY) === ROOT_QUERY) {
      this.dropExpiredRootFields(options);
    }
    const ref = super.write(options);
    if (this.optimisticDepth === 0 && result && (options.dataId ?? ROOT_QUERY) === ROOT_QUERY) {
      if (fromNetwork) this.stampNetworkRootFields(options);
      else this.seedLocalRootFields(options);
    }
    return ref;
  }

  batch<TUpdateResult>(options: Cache.BatchOptions<InMemoryCache, TUpdateResult>): TUpdateResult {
    // Una capa optimista no siembra edades: desaparece al llegar la respuesta.
    if (typeof options.optimistic !== 'string') return super.batch(options);
    this.optimisticDepth += 1;
    try {
      return super.batch(options);
    } finally {
      this.optimisticDepth -= 1;
    }
  }

  /**
   * Lo llama `FreshnessLink` con el `result.data` de cada respuesta de RED. Solo
   * marca el objeto: la edad se pone en `write`, con el documento de la caché.
   */
  markFromNetwork(data: object): void {
    if (data && typeof data === 'object') this.networkResults.add(data);
  }

  extract(optimistic = false): NormalizedCacheObject {
    const data = super.extract(optimistic);
    const snapshot: FreshnessSnapshot = { v: 1, roots: Object.fromEntries(this.rootStamps) };
    // Va SIEMPRE, aunque esté vacío: siete loaders miran `Object.keys(extract())`
    // y con la librería nunca era 0 (llevaba `invalidation`). Medido en el spike.
    return { ...data, [FRESHNESS_SNAPSHOT_KEY]: snapshot as unknown as StoreObject };
  }

  restore(data: NormalizedCacheObject): this {
    const {
      [FRESHNESS_SNAPSHOT_KEY]: snapshot,
      invalidation,
      ...store
    } = (data ?? {}) as NormalizedCacheObject & {
      [FRESHNESS_SNAPSHOT_KEY]?: FreshnessSnapshot;
      invalidation?: LegacyInvalidation;
    };
    this.rootStamps = new Map(validRoots(snapshot));
    this.entityStamps = new Map();
    // Snapshot (o semilla de IndexedDB) de la versión con la librería: sus
    // edades se conservan. Sin esto, `invalidation` entraría como una entidad.
    if (invalidation) this.importLegacy(invalidation, store);
    this.markUnknownAgesExpired(store as NormalizedCacheObject);
    super.restore(store as NormalizedCacheObject);
    this.deriveEntityStamps(store as NormalizedCacheObject);
    return this;
  }

  reset(options?: Cache.ResetOptions): Promise<void> {
    this.rootStamps.clear();
    this.entityStamps.clear();
    return super.reset(options);
  }

  /**
   * Retención: quita los campos raíz que llevan más de `maxAgeMs` sin una
   * respuesta de red y recoge lo que deja huérfano. Es lo único que borra datos
   * por edad. Devuelve cuántos campos raíz y entidades se han ido.
   */
  pruneRetention(maxAgeMs: number): { evictedRoots: number; removedEntities: number } {
    const limit = Date.now() - maxAgeMs;
    // Edades de campos que ya no existen (evict, modify(DELETE), gc): se olvidan
    // y no cuentan como retiradas por edad.
    const store = super.extract(false);
    const root = (store[ROOT_QUERY] ?? {}) as Record<string, unknown>;
    for (const storeFieldName of [...this.rootStamps.keys()]) {
      if (!(storeFieldName in root)) this.rootStamps.delete(storeFieldName);
    }
    // Lo mismo con las entidades que la app ya quitó (evict, gc): con el
    // WebView abierto días, el mapa crecería sin fin.
    for (const id of [...this.entityStamps.keys()]) {
      if (!(id in store)) this.entityStamps.delete(id);
    }
    const expired = new Set<string>();
    for (const [storeFieldName, stamp] of this.rootStamps) {
      if (stamp < limit) expired.add(storeFieldName);
    }
    // Borra SOLO esas combinaciones de filtros (ver `deleteRootFields`).
    this.deleteRootFields(expired);
    for (const storeFieldName of expired) this.rootStamps.delete(storeFieldName);
    const removed = expired.size > 0 ? this.gc() : [];
    for (const id of removed) this.entityStamps.delete(id);
    return { evictedRoots: expired.size, removedEntities: removed.length };
  }

  protected broadcastWatches(
    options?: Pick<Cache.BatchOptions<InMemoryCache>, 'optimistic' | 'onWatchUpdated'>,
  ): void {
    const previous = this.broadcasting;
    this.broadcasting = true;
    try {
      super.broadcastWatches(options);
    } finally {
      this.broadcasting = previous;
    }
  }

  private withDefaults(query: DocumentNode, variables?: Record<string, unknown>): Record<string, unknown> {
    return { ...getDefaultValues(getOperationDefinition(query)), ...(variables ?? {}) };
  }

  private storeFieldNameOf(field: FieldNode, fieldName: string, variables: Record<string, unknown>): string {
    return this.policies.getStoreFieldName({ typename: 'Query', fieldName, field, variables });
  }

  /** La edad del campo raíz más viejo de la consulta; `undefined` si no se sabe. */
  private oldestStampFor(
    query: DocumentNode,
    variables: Record<string, unknown> | undefined,
    result: Record<string, unknown> | undefined,
  ): number | undefined {
    const vars = this.withDefaults(query, variables);
    let oldest: number | undefined;
    for (const { field, fieldName, resultKey } of topLevelFields(query)) {
      let stamp = this.rootStamps.get(this.storeFieldNameOf(field, fieldName, vars));
      if (stamp === undefined) {
        // Sin campo propio (lo resuelve un `read` con `toReference`): manda la
        // edad de la entidad a la que apunta. Un campo `@client` no tiene
        // ninguna de las dos y no opina.
        const value = result?.[resultKey];
        const id =
          value && typeof value === 'object' && !Array.isArray(value)
            ? this.identify(value as StoreObject)
            : undefined;
        stamp = id ? this.entityStamps.get(id) : undefined;
      }
      if (stamp !== undefined && (oldest === undefined || stamp < oldest)) oldest = stamp;
    }
    return oldest;
  }

  /**
   * Escritura local de un campo raíz que no tenía edad (sembrado por la app, o
   * el respaldo reescribiendo un detalle sacado de la lista): toma la de la
   * entidad más vieja que contiene; sin entidades conocidas, es dato nuevo.
   * Un campo que YA tenía edad no se toca: lo local nunca rejuvenece.
   */
  private seedLocalRootFields(options: Cache.WriteOptions): void {
    const result = options.result as Record<string, unknown>;
    const vars = this.withDefaults(options.query, options.variables as Record<string, unknown> | undefined);
    for (const { field, fieldName, resultKey } of topLevelFields(options.query)) {
      if (!(resultKey in result)) continue;
      const storeFieldName = this.storeFieldNameOf(field, fieldName, vars);
      if (this.rootStamps.has(storeFieldName)) continue;
      this.rootStamps.set(storeFieldName, this.oldestEntityStampIn(result[resultKey]) ?? Date.now());
    }
  }

  /** Escritura de una respuesta de red marcada: sella campos raíz y entidades con «ahora». */
  /**
   * Respuesta de red para un campo raíz CADUCADO: se quita antes de escribirla,
   * como hacía la librería al leer. Sin esto, el `merge` de las listas
   * paginadas conservaría lo viejo y la edad nueva lo daría por fresco.
   */
  private dropExpiredRootFields(options: Cache.WriteOptions): void {
    const limit = Date.now() - this.freshnessMs;
    const result = options.result as Record<string, unknown>;
    const vars = this.withDefaults(options.query, options.variables as Record<string, unknown> | undefined);
    const expired = new Set<string>();
    for (const { field, fieldName, resultKey } of topLevelFields(options.query)) {
      if (!(resultKey in result)) continue;
      const storeFieldName = this.storeFieldNameOf(field, fieldName, vars);
      const stamp = this.rootStamps.get(storeFieldName);
      if (stamp !== undefined && stamp < limit) expired.add(storeFieldName);
    }
    this.deleteRootFields(expired);
  }

  /**
   * Borra EXACTAMENTE esas claves de ROOT_QUERY. `evict({ fieldName })` no
   * sirve: Apollo casa el modificador por `fields[storeFieldName] ||
   * fields[fieldName]` (`entityStore.js`), así que quitar `campo` (sin
   * argumentos) se llevaría también `campo({...})` de todos los filtros.
   */
  private deleteRootFields(storeFieldNames: Set<string>): void {
    if (storeFieldNames.size === 0) return;
    this.modify({
      id: ROOT_QUERY,
      fields: (value, { storeFieldName, DELETE }) => (storeFieldNames.has(storeFieldName) ? DELETE : value),
      broadcast: false,
    });
  }

  private stampNetworkRootFields(options: Cache.WriteOptions): void {
    const now = Date.now();
    const result = options.result as Record<string, unknown>;
    const vars = this.withDefaults(options.query, options.variables as Record<string, unknown> | undefined);
    for (const { field, fieldName, resultKey } of topLevelFields(options.query)) {
      if (!(resultKey in result)) continue;
      this.rootStamps.set(this.storeFieldNameOf(field, fieldName, vars), now);
    }
    this.stampEntities(result, now);
  }

  private forEachEntity(value: unknown, visit: (id: string) => void): void {
    if (Array.isArray(value)) {
      for (const item of value) this.forEachEntity(item, visit);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const object = value as Record<string, unknown>;
    if (typeof object.__typename === 'string') {
      const id = this.identify(object as StoreObject);
      if (id) visit(id);
    }
    for (const key in object) {
      const child = object[key];
      if (child && typeof child === 'object') this.forEachEntity(child, visit);
    }
  }

  private oldestEntityStampIn(value: unknown): number | undefined {
    let oldest: number | undefined;
    this.forEachEntity(value, (id) => {
      const stamp = this.entityStamps.get(id);
      if (stamp !== undefined && (oldest === undefined || stamp < oldest)) oldest = stamp;
    });
    return oldest;
  }

  private stampEntities(value: unknown, now: number): void {
    this.forEachEntity(value, (id) => this.entityStamps.set(id, now));
  }

  private importLegacy(invalidation: LegacyInvalidation, store: NormalizedCacheObject): void {
    const root = (store[ROOT_QUERY] ?? {}) as Record<string, unknown>;
    for (const entity of Object.values(invalidation.entitiesById ?? {})) {
      if (entity?.dataId !== ROOT_QUERY) continue;
      for (const [storeFieldName, entry] of Object.entries(entity.storeFieldNames?.entries ?? {})) {
        // La librería apuntaba también claves que no existen en el almacén.
        if (typeof entry?.cacheTime === 'number' && storeFieldName in root) {
          this.rootStamps.set(storeFieldName, entry.cacheTime);
        }
      }
    }
  }

  /**
   * Un campo raíz sin edad (snapshot sin registro o con uno inválido, entradas
   * de la librería sin `cacheTime`) se da por recién caducado: se vuelve a
   * pedir en la primera lectura con red, se sirve sin ella y la retención lo
   * quita a los 7 días. Sin esto no caducaría ni se podaría nunca.
   */
  private markUnknownAgesExpired(store: NormalizedCacheObject): void {
    const root = (store[ROOT_QUERY] ?? {}) as Record<string, unknown>;
    const expiredNow = Date.now() - this.freshnessMs - 1;
    for (const storeFieldName of Object.keys(root)) {
      if (storeFieldName === '__typename' || this.rootStamps.has(storeFieldName)) continue;
      this.rootStamps.set(storeFieldName, expiredNow);
    }
  }

  /**
   * Una entidad es tan fresca como el campo raíz más fresco que llega a ella.
   * Se recorre del campo más fresco al más viejo con UN solo «visto»: la
   * primera visita ya pone la edad máxima, así que no hace falta volver a
   * entrar (antes, una vez por campo raíz, dentro del splash). Sin `push(...)`:
   * con un array enorme lanzaría RangeError y se perdería el snapshot.
   */
  private deriveEntityStamps(store: NormalizedCacheObject): void {
    const root = (store[ROOT_QUERY] ?? {}) as Record<string, unknown>;
    const byFreshness = [...this.rootStamps].sort((a, b) => b[1] - a[1]);
    const seen = new Set<string>();
    for (const [storeFieldName, stamp] of byFreshness) {
      const pending: unknown[] = [root[storeFieldName]];
      while (pending.length > 0) {
        const value = pending.pop();
        if (Array.isArray(value)) {
          for (const item of value) pending.push(item);
          continue;
        }
        if (!value || typeof value !== 'object') continue;
        if (isReference(value)) {
          const id = value.__ref;
          if (seen.has(id)) continue;
          seen.add(id);
          const previous = this.entityStamps.get(id);
          if (previous === undefined || previous < stamp) this.entityStamps.set(id, stamp);
          pending.push(store[id]);
          continue;
        }
        for (const key in value as Record<string, unknown>) pending.push((value as Record<string, unknown>)[key]);
      }
    }
  }
}
